import moment from "moment";
import React, { useMemo, useState } from "react";
import ResponsivePaginationComponent from "react-responsive-pagination";
import "react-responsive-pagination/themes/classic.css";

import CustomTable from "components/common/CustomTable";
import EmptyStateComp from "components/common/EmptyStateComp";
import TextInput from "components/common/InputField/TextInput";
import { formatMoney } from "helpers";
import { monifyInputField } from "helpers/monifyInputField";
import useGetBundleDashboardConfigListV2 from "hooks/Dashboard/useGetBundleDashboardConfigListV2";
import { ForecastMethodEnum } from "./enums/ForecastMethodEnum";
import clsx from "clsx";
import SelectInput from "components/common/InputField/SelectInput";
import { ForecastConfigurationTableContentProps } from "./types/forcastConfig";

type ForecastConfigurationBundleTableProps = ForecastConfigurationTableContentProps;

const ForecastConfigurationBundleTable = (props: ForecastConfigurationBundleTableProps) => {
    const {
        categoryFilter,
        channelFilter,
        productIds,
        chartFilter,
        handleConfigChange,
        monthlyConfigChange,
        weeklyConfigChange,
        forecastMethodOptions,
        monthlyTab,
        activePeriod,
    } = props;

    const [isScrolled, setIsScrolled] = useState<boolean>(false);

    const handleScroll = (e) => {
        if (e.target.scrollLeft > 0) {
            setIsScrolled(true);
        } else {
            setIsScrolled(false);
        }
    };

    const [page, setPage] = useState<number>(1);

    // const { data: gwmTooltip, handleStartAndEndDateChange: handleTooltipStartAndEndDateChange } =
    //     useGetBundleDashboardGWMTooltip();

    const {
        data: configList,
        isFetching: configListLoading,
        pagination: configPagination,
    } = useGetBundleDashboardConfigListV2(
        useMemo(
            () => ({
                params: {
                    page: page,
                    dateRange: "yearly",
                    productId: productIds?.join(","),
                    channel: channelFilter,
                    category: "",
                },
            }),
            [productIds, channelFilter, categoryFilter, page]
        )
    );

    const configureForecastTableHeader = [
        {
            title: "Bundle SKU",
            widthClass: "w-[136px] !border-r-none",
        },
        { title: "SKU components", widthClass: "w-[136px] !border-r-none" },
        { title: "Unit/SKU", widthClass: "w-[106px] !border-r-none" },
        { title: "Actual sale/Bundle", widthClass: "w-[130px] !border-r-none" },
        { title: "Actual sale/SKU", widthClass: "w-[116px] !border-r-none" },
        // { title: "Growth rate/SKU", widthClass: "w-[116px] !border-r-none" },
        { title: "Forecast Method", widthClass: "w-[200px] !border-r-none" },
        { title: "Adjusted Demand forecast/Bundle", widthClass: "w-[200px] !border-r-none" },
        { title: "Demand forecast/Bundle", widthClass: "w-[177px] !border-r-none" },
        { title: "Adjusted Demand forecast/SKU", widthClass: "w-[160px] !border-r-none" },
        { title: "Demand forecast/SKU", widthClass: "w-[147px] !border-r-none" },
        { title: "Adjusted COGS forecast", widthClass: "w-[124px] !border-r-none" },
        { title: "COGS forecast", widthClass: "w-[106px] !border-r-none" },
        { title: "Adjusted Revenue forecast", widthClass: "w-[160px] !border-r-none" },
        { title: "Revenue forecast", widthClass: "w-[122px] !border-r-none" },
        { title: "Current stock level/SKU", widthClass: "w-[157px] !border-r-none" },
        { title: "Stock deficit/SKU", widthClass: "w-[126px] !border-r-none" },
        // { title: "Replenishment due", widthClass: "w-[136px] !border-r-none" },
    ];

    const configureForecastTableBody = configList?.map((product, idx) => {
        const currMonth = moment()?.format("MMM-YYYY");
        const currentKeyFormat = monthlyTab?.[activePeriod]?.split(" ")?.join("-");

        const currentConfigObj = product?.configs?.find?.((item) => {
            return chartFilter === "yearly" ? item?.[currentKeyFormat] : item?.[currMonth];
        });

        const currentConfigValues =
            chartFilter === "yearly"
                ? currentConfigObj?.[currentKeyFormat]?.[0]
                : currentConfigObj?.[currMonth]?.[activePeriod];

        const currForecastData =
            chartFilter === "yearly"
                ? product?.estinmatedSalesForecast?.monthlyForecast?.find?.((item) => {
                      return item?.yearMonth === currentKeyFormat;
                  })
                : product?.estinmatedSalesForecast?.weeklyForecast?.filter?.((item) => {
                      return item?.yearMonth === currMonth;
                  });

        const currDemandForecast =
            chartFilter === "yearly"
                ? String(Math.round(currForecastData?.forecastedSale || 0))
                : String(Math.round(currForecastData?.[activePeriod]?.forecastedSale || 0));

        const configCopy = [
            ...(chartFilter === "yearly" ? monthlyConfigChange : weeklyConfigChange),
        ];

        const changedConfig = configCopy.find((conf) => {
            return (
                conf?.[chartFilter === "yearly" ? monthlyTab?.[activePeriod] : currMonth]
                    ?.inventoryId === product?.inventory?._id
            );
        });

        const changeValue = Math.round(
            chartFilter === "yearly"
                ? changedConfig?.[monthlyTab?.[activePeriod]]?.monthlyDemand?.quantity
                : changedConfig?.[currMonth]?.weeklyDemand?.[activePeriod]?.quantity
        );

        const defaultValue = Math.round(
            chartFilter === "yearly"
                ? currentConfigValues?.monthlyDemand?.quantity || ""
                : currentConfigValues?.weeklyDemand?.quantity || ""
        );

        const changeForecastMethodValue =
            chartFilter === "yearly"
                ? changedConfig?.[monthlyTab?.[activePeriod]]?.forecastMethod
                : changedConfig?.[currMonth]?.forecastMethod;

        const defaultForecastMethodValue =
            product?.inventory?.forecastSettings?.[channelFilter]?.forecastMethod ||
            ForecastMethodEnum.synth;

        const selectedForecastMethod = forecastMethodOptions.find(
            (item) => item.value === (changeForecastMethodValue || defaultForecastMethodValue)
        );

        const adjustedValue = Number(changeValue || defaultValue || 0);

        return {
            groupContent: (
                <div
                    className={`w-full py-2 border-slate-100 text-sm font-rocGroteskMedium text-slate-900 `}
                >
                    <div className="font-rocGroteskMedium   items-center flex space-x-3">
                        <div className="text-sm font-rocGroteskMedium">
                            <p className="text-g-75 ">{product?.inventory?.sku}</p>
                            <p className="text-slate-500">{product?.inventory?.productName}</p>
                        </div>
                    </div>
                </div>
            ),
            associatedSKUs: product?.inventory?.bundleItems?.map((associatedSku) => {
                return [
                    {
                        content: (
                            <div
                                key={1}
                                className={`w-full py-2 border-slate-100 text-sm font-rocGroteskMedium text-slate-900 `}
                            >
                                <div className="text-sm font-rocGroteskMedium">
                                    <p className="text-g-75 ">{associatedSku?.inventory?.sku}</p>
                                    <p className="text-slate-500 ">
                                        {associatedSku?.inventory?.productName}
                                    </p>
                                </div>
                            </div>
                        ),
                    },
                    {
                        content: (
                            <div
                                className={`w-full py-2 font-rocGroteskMedium border-slate-100 text-sm text-g-75 `}
                            >
                                <p>{formatMoney().format(associatedSku?.quantity)}</p>
                            </div>
                        ),
                    },
                    {
                        content: (
                            <div
                                className={`w-full py-2 font-rocGroteskMedium border-slate-100 text-sm text-g-75 `}
                            >
                                <p>${formatMoney().format(associatedSku?.totalActualSales)}</p>
                            </div>
                        ),
                        // widthClass: `!border-none`,
                        shouldRowSpan: true,
                    },
                    {
                        content: (
                            <div
                                className={`w-full py-2 font-rocGroteskMedium border-slate-100 text-sm text-g-75 `}
                            >
                                <p>
                                    $
                                    {formatMoney().format(
                                        associatedSku?.totalActualSales * associatedSku?.quantity
                                    )}
                                </p>
                            </div>
                        ),
                        // widthClass: `!border-none`,
                    },
                    {
                        content: (
                            <div
                                className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600 w-full py-2`}
                            >
                                <SelectInput
                                    value={changeForecastMethodValue || defaultForecastMethodValue}
                                    name="forecastMethod"
                                    idx={idx}
                                    toolTipController={{
                                        content: selectedForecastMethod ? (
                                            <div className="flex flex-col gap-1 font-rocGroteskMedium">
                                                <p>{selectedForecastMethod.label}</p>
                                                <p className="text-slate-400">
                                                    {selectedForecastMethod.description}
                                                </p>
                                            </div>
                                        ) : (
                                            "testing"
                                        ),
                                        className:
                                            "!w-[500px] !max-w-[374px] !p-[12px] !rounded-[8px]",
                                        place: "top",
                                        style: { maxWidth: "250px", zIndex: 9999 },
                                    }}
                                    placeholder={"Select forecast method"}
                                    isRequired={true}
                                    handleChange={(_, value) => {
                                        handleConfigChange(
                                            {
                                                adjustedDemandForecast: monifyInputField(
                                                    changeValue >= 0 ? changeValue : defaultValue
                                                ),
                                                forecastMethod: value,
                                            },
                                            product?.inventory,
                                            currentConfigObj?.[currMonth]
                                        );
                                    }}
                                    dropdownClassName="!w-[500px] !max-w-[500px] !p-0"
                                    // inputContainerClassName="!p-0"
                                    // optionItemClassName="!p-0"
                                    optionItemContainerClassName="!p-0"
                                    activeOptionItemClassName="!hidden"
                                    activeOptionItemContainerClassName="!p-0"
                                    optionItemClassName="!p-0"
                                    dropdownOptions={forecastMethodOptions.map((item) => ({
                                        label: (
                                            <div
                                                className={clsx(
                                                    "flex flex-col w-full w-[500px] gap-1 px-[16px] py-[10px]",
                                                    item.value ===
                                                        (changeForecastMethodValue ||
                                                            defaultForecastMethodValue) &&
                                                        "bg-[#F1F5F9]"
                                                )}
                                            >
                                                <p>{item.label}</p>
                                                <p className="text-xs text-slate-500">
                                                    {item.description}
                                                </p>
                                            </div>
                                        ),
                                        value: item.value,
                                    }))}
                                />
                            </div>
                        ),
                        tableDataDivClass: "!pl-0",
                        shouldRowSpan: true,
                    },
                    {
                        content: (
                            <div
                                className={`!font-rocGroteskMedium border-slate-100 text-sm text-slate-600 w-full py-2`}
                            >
                                <TextInput
                                    name={"adjustedDemandForecast"}
                                    type={"text"}
                                    onWheel={(e: any) => e.target.blur()}
                                    value={monifyInputField(
                                        changeValue >= 0 ? changeValue : defaultValue
                                    )}
                                    onChange={(e) => {
                                        handleConfigChange(
                                            {
                                                adjustedDemandForecast: monifyInputField(
                                                    e.target.value
                                                ),
                                                forecastMethod:
                                                    changeForecastMethodValue ||
                                                    defaultForecastMethodValue,
                                            },
                                            product?.inventory,
                                            currentConfigObj?.[currMonth]
                                        );
                                    }}
                                    placeholder={"Adjusted Demand"}
                                    required={true}
                                    onKeyDown={(evt) =>
                                        ["e", "E", "+", "-", ".", "ArrowUp", "ArrowDown"].includes(
                                            evt.key
                                        ) && evt.preventDefault()
                                    }
                                    pattern={"^[0-9]+$"}
                                    title={"must be digits"}
                                />
                            </div>
                        ),
                        tableDataDivClass: "!pl-0",
                        shouldRowSpan: true,
                    },
                    {
                        content: (
                            <div
                                className={`w-full py-2 font-rocGroteskMedium border-slate-100 text-sm text-g-75 `}
                            >
                                <p>{formatMoney().format(currDemandForecast)}</p>
                            </div>
                        ),
                        // widthClass: `!border-none`,
                        shouldRowSpan: true,
                    },
                    {
                        content: (
                            <div
                                className={`w-full py-2 font-rocGroteskMedium border-slate-100 text-sm text-g-75 `}
                            >
                                <p>
                                    {formatMoney().format(
                                        Number(adjustedValue) * associatedSku?.quantity
                                    )}
                                </p>
                            </div>
                        ),
                        // widthClass: `!border-none`,
                    },
                    {
                        content: (
                            <div
                                className={`w-full py-2 font-rocGroteskMedium border-slate-100 text-sm text-g-75 `}
                            >
                                <p>
                                    {formatMoney().format(
                                        Number(currDemandForecast) * associatedSku?.quantity
                                    )}
                                </p>
                            </div>
                        ),
                        // widthClass: `!border-none`,
                    },
                    {
                        content: (
                            <div
                                className={`w-full py-2 font-rocGroteskMedium border-slate-100 text-sm text-g-75 `}
                            >
                                <p>
                                    $
                                    {formatMoney().format(
                                        Number(adjustedValue) *
                                            Number(associatedSku?.inventory?.unitCost?.amount)
                                    )}
                                </p>
                            </div>
                        ),
                        // widthClass: `!border-none`,
                    },
                    {
                        content: (
                            <div
                                className={`w-full py-2 font-rocGroteskMedium border-slate-100 text-sm text-g-75 `}
                            >
                                <p>
                                    $
                                    {formatMoney().format(
                                        Number(currDemandForecast) *
                                            Number(associatedSku?.inventory?.unitCost?.amount)
                                    )}
                                </p>
                            </div>
                        ),
                        // widthClass: `!border-none`,
                    },
                    {
                        content: (
                            <div
                                className={`w-full py-2 font-rocGroteskMedium border-slate-100 text-sm text-g-75 `}
                            >
                                <p>
                                    $
                                    {formatMoney().format(
                                        Number(adjustedValue) *
                                            Number(associatedSku?.inventory?.salesPrice?.amount)
                                    )}
                                </p>
                            </div>
                        ),
                        // widthClass: `!border-none`,
                    },
                    {
                        content: (
                            <div
                                className={`w-full py-2 font-rocGroteskMedium border-slate-100 text-sm text-g-75 `}
                            >
                                <p>
                                    $
                                    {formatMoney().format(
                                        Number(currDemandForecast) *
                                            Number(associatedSku?.inventory?.salesPrice?.amount)
                                    )}
                                </p>
                            </div>
                        ),
                        // widthClass: `!border-none`,
                    },
                    {
                        content: (
                            <div
                                className={`w-full py-2 font-rocGroteskMedium border-slate-100 text-sm text-g-75 `}
                            >
                                <p>
                                    {formatMoney().format(
                                        Number(
                                            associatedSku?.inventory?.productStockDetails
                                                ?.stockLevel
                                        )
                                    )}
                                </p>
                            </div>
                        ),
                        // widthClass: `!border-none`,
                    },
                    {
                        content: (
                            <div
                                className={`w-full py-2 font-rocGroteskMedium border-slate-100 text-sm text-g-75 `}
                            >
                                <p>
                                    {formatMoney().format(
                                        Number(
                                            associatedSku?.inventory?.productStockDetails
                                                ?.stockLevel
                                        ) -
                                            Number(currDemandForecast) * associatedSku?.quantity
                                    )}
                                </p>
                            </div>
                        ),
                    },
                ];
            }),
        };
    });

    return (
        <div>
            <div className="overflow-x-scroll" onScroll={handleScroll}>
                <CustomTable
                    groupedData={configureForecastTableBody}
                    tableHeader={configureForecastTableHeader}
                    isScrollable={true}
                    isScrolled={isScrolled}
                    isCellBordered={true}
                    scrollWithin={true}
                    isInifiniteScroll={false}
                    isCellSelectable={false}
                    isAllSelectable={false}
                    isGrouped={true}
                    tableContainerClass="!max-h-[427px]"
                    headerContainerClass="!bg-slate-50"
                    headerItemClass="font-rocGroteskMedium !text-xs !text-slate-700"
                    tableClass="!border !border-slate-200 cursor-pointer"
                    bodyItemClass="!border-slate-200 !cursor-default hover:!bg-slate-50"
                    isLoading={configListLoading}
                    renderEmptyTable={() => (
                        <EmptyStateComp
                            title="No products available"
                            description={`There are no products available `}
                            icon={<i className="ri-archive-2-line text-[48px] text-slate-400"></i>}
                        />
                    )}
                />
            </div>

            <div className="flex justify-end mt-5">
                <ResponsivePaginationComponent
                    current={Number(configPagination?.currentPage)}
                    total={Number(configPagination?.noOfPages)}
                    maxWidth={350}
                    onPageChange={(page) => setPage(page)}
                />
            </div>
        </div>
    );
};

export default ForecastConfigurationBundleTable;
